/**
 * PROTOTYPE. Throwaway view model for the Diagnostics redesign variants.
 *
 * Three variants of Settings > Diagnostics, switchable with `?variant=A|B|C`
 * on the existing route. This file turns the cloud page state into plain facts
 * so each variant can disagree about layout without re-deriving anything.
 */
import type {
  CmmdCloudProjectLifecyclePreflight,
  CmmdRuntimeDiagnosticsSnapshot,
  RuntimeDiagnosticsResponse,
} from "@cmmd-forge/contracts";

import type { RuntimeAuthorityRenewalState } from "../../../environments/runtime/cloudRuntimeAuthorityRenewal";
import { cloudProviderStatus } from "../DiagnosticsCloudProviderStatus";
import {
  makeCloudControlPlaneSnapshot,
  type CloudRuntimeView,
} from "../DiagnosticsCloudRuntimes.logic";
import type { StatusTone } from "../DiagnosticsSettings.types";
import type { DiagnosticsTargetOption } from "../DiagnosticsTargetPicker.logic";
import type { CodexRuntimeReadiness } from "../codexRuntimeReadiness";
import {
  lifecycleFromCatalogStatus,
  projectDiagnosticsSeatHealth,
  type DiagnosticsSeatHealth,
} from "../diagnosticsProductHealth";
import { formatTimestamp, shortId, state, toneForState } from "./diagnosticsPrototypeFormat";
import { cloudTimeline, receiptGroup } from "./diagnosticsPrototypeReceipt";
import type {
  DiagnosticsPrototypeModel,
  PrototypeEvent,
  PrototypeFact,
  PrototypeFactGroup,
  PrototypeVerdict,
} from "./diagnosticsPrototypeTypes";

export type * from "./diagnosticsPrototypeTypes";
export { buildDesktopModel, buildServerModel } from "./diagnosticsPrototypeModelLocal";

type Snapshot = CmmdRuntimeDiagnosticsSnapshot | undefined;

const HEALTH_TONE: Readonly<Record<DiagnosticsSeatHealth["state"], StatusTone>> = {
  ready: "ready",
  paused: "warning",
  recover: "error",
  sleeping: "muted",
  unknown: "muted",
};

function authorityFact(auth: RuntimeAuthorityRenewalState | null): PrototypeFact {
  const label = "Provider authority";
  if (auth === null) return { label, value: "not observed", tone: "muted" };
  if (auth.kind === "scheduled") {
    return { label, value: `live, renews ${formatTimestamp(auth.renewAt)}`, tone: "ready" };
  }
  if (auth.kind === "renewing") return { label, value: "renewing", tone: "warning" };
  if (auth.kind === "ready") {
    return { label, value: `renewed ${formatTimestamp(auth.renewedAt)}`, tone: "ready" };
  }
  const rejected = auth.code === "authority_auth_rejected";
  return {
    label,
    value: rejected ? "sign in again" : "retrying",
    tone: rejected ? "error" : "warning",
  };
}

function credentialsFact(snapshot: Snapshot): PrototypeFact {
  if (snapshot === undefined) return { label: "Credentials", value: "not reported", tone: "muted" };
  const epoch = snapshot.authority.credentialEpoch ?? "unknown";
  const attested = snapshot.authority.attestedCredentialEpoch;
  const agrees = snapshot.authority.credentialEpoch === attested;
  return {
    label: "Credentials",
    value: agrees ? `epoch ${epoch}, attested` : `epoch ${epoch}, attested ${attested ?? "none"}`,
    tone: agrees ? "ready" : "warning",
  };
}

function forgeBuildFact(snapshot: Snapshot): PrototypeFact {
  const installed = snapshot?.forge.installedCommit ?? null;
  const desired = snapshot?.forge.desiredCommit ?? null;
  const drifted = installed !== null && desired !== null && installed !== desired;
  return {
    label: "Forge build",
    value: drifted
      ? `${installed.slice(0, 10)} wants ${desired.slice(0, 10)}`
      : (installed?.slice(0, 10) ?? "not reported"),
    mono: true,
    tone: drifted ? "warning" : toneForState(snapshot?.forge.state, ["current"]),
  };
}

function machineGroup(runtime: CloudRuntimeView, snapshot: Snapshot): PrototypeFactGroup {
  return {
    id: "machine",
    title: "Machine",
    source: `CMMD control plane, ${formatTimestamp(snapshot?.availability.observedAt)}`,
    observedAt: snapshot?.availability.observedAt ?? null,
    facts: [
      {
        label: "Lifecycle",
        value: state(snapshot?.lifecycle.state),
        tone: toneForState(snapshot?.lifecycle.state, ["running"]),
      },
      {
        label: "Service",
        value: state(snapshot?.service.state),
        tone: toneForState(snapshot?.service.state, ["ready"]),
      },
      {
        label: "Last failure",
        value: state(snapshot?.failure.classification),
        tone: snapshot?.failure.classification === "none" ? "ready" : "warning",
      },
      {
        label: "Replication",
        value: state(snapshot?.replication.state),
        tone: toneForState(snapshot?.replication.state, ["available"]),
      },
      { label: "Runtime", value: shortId(runtime.runtimeId), mono: true },
    ],
  };
}

function buildGroup(
  snapshot: Snapshot,
  authority: RuntimeAuthorityRenewalState | null,
  readiness: CodexRuntimeReadiness,
): PrototypeFactGroup {
  const delivery = cloudProviderStatus({ codexReadiness: readiness, sessionAuthState: "unknown" });
  return {
    id: "build",
    title: "Build and credentials",
    source: `CMMD control plane, ${formatTimestamp(snapshot?.availability.observedAt)}`,
    observedAt: snapshot?.availability.observedAt ?? null,
    facts: [
      forgeBuildFact(snapshot),
      credentialsFact(snapshot),
      authorityFact(authority),
      { label: "Codex delivery", value: delivery.state, tone: delivery.tone },
    ],
  };
}

function inventoryGroup(inventory: RuntimeDiagnosticsResponse): PrototypeFactGroup {
  const failing = inventory.tools.filter((tool) => tool.status === "error").length;
  return {
    id: "operator",
    title: "Machine inventory",
    source: `Probed on the Sprite, ${formatTimestamp(inventory.fetchedAt)}`,
    observedAt: inventory.fetchedAt,
    facts: [
      { label: "Processes", value: String(inventory.processes.length) },
      {
        label: "Tools",
        value: failing === 0 ? `${inventory.tools.length} ok` : `${failing} failing`,
        tone: failing === 0 ? "ready" : "error",
      },
      { label: "Machine", value: inventory.machineId, mono: true },
    ],
  };
}

function cloudVerdict(input: {
  readonly health: DiagnosticsSeatHealth;
  readonly catalogSays: string | null;
  readonly snapshot: Snapshot;
}): PrototypeVerdict {
  const snapshotSays = input.snapshot?.lifecycle.state ?? null;
  const disagree =
    input.catalogSays !== null && snapshotSays !== null && input.catalogSays !== snapshotSays;
  return {
    label: input.health.label,
    sentence: input.health.detail,
    tone: HEALTH_TONE[input.health.state],
    observedAt: input.snapshot?.availability.observedAt ?? null,
    source: "CMMD control plane. Reading this never wakes the Environment.",
    disagreement: disagree
      ? `The catalog says ${input.catalogSays}; the last snapshot says ${snapshotSays}. Refresh to reconcile.`
      : null,
  };
}

export function buildCloudModel(input: {
  readonly target: DiagnosticsTargetOption | null;
  readonly runtime: CloudRuntimeView;
  readonly snapshot: Snapshot;
  readonly receipt: CmmdCloudProjectLifecyclePreflight | undefined;
  readonly authority: RuntimeAuthorityRenewalState | null;
  readonly readiness: CodexRuntimeReadiness;
  readonly inventory: RuntimeDiagnosticsResponse | undefined;
  readonly events: ReadonlyArray<PrototypeEvent>;
  readonly isPlatformAdmin: boolean;
}): DiagnosticsPrototypeModel {
  const bound = makeCloudControlPlaneSnapshot({ runtime: input.runtime, snapshot: input.snapshot });
  const snapshot = bound.collectionOutcome === "matched" ? input.snapshot : undefined;
  const lifecycle =
    input.runtime.lifecycle ?? lifecycleFromCatalogStatus(input.runtime.catalogStatus);
  const health = projectDiagnosticsSeatHealth({ lifecycle, snapshot });
  const groups: PrototypeFactGroup[] = [
    machineGroup(input.runtime, snapshot),
    buildGroup(snapshot, input.authority, input.readiness),
    receiptGroup(input.receipt),
  ];
  if (input.isPlatformAdmin && input.inventory) groups.push(inventoryGroup(input.inventory));
  const snapshotAt = snapshot?.availability.observedAt ?? null;
  const receiptAt = input.receipt?.observedAt ?? null;
  return {
    target: input.target,
    verdict: cloudVerdict({ health, catalogSays: lifecycle?.state ?? null, snapshot }),
    groups,
    checks: groups.flatMap((group) =>
      group.facts.map((fact) => ({
        group: group.title,
        name: fact.label,
        result: fact.value,
        tone: fact.tone ?? "muted",
        observedAt: group.id === "receipt" ? receiptAt : snapshotAt,
      })),
    ),
    timeline: cloudTimeline(input.receipt, snapshot),
    events: input.events,
    isPlatformAdmin: input.isPlatformAdmin,
  };
}
